import React, { useState } from 'react';
import './PayloiProduct.css';

const solutionPoints = [
  { id: 'sp0', index: 0 },
  { id: 'sp1', index: 1 },
  { id: 'sp2', index: 2 },
  { id: 'sp3', index: 3 },
  { id: 'sp4', index: 4 },
  { id: 'sp5', index: 5 }
];

function PayloiProduct() {
  const [activeCard, setActiveCard] = useState(null);

  const updateActivePoint = (currentIndex) => {
    solutionPoints.forEach((point, idx) => {
      const element = document.getElementById(point.id);
      if (element) {
        element.classList.toggle('active', idx === currentIndex);
      }
    });
  };

  const handleCardClick = (index) => {
    setActiveCard(`sp${index}`);

    const targetElement = document.getElementById(`card-${index}`);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    updateActivePoint(index);

    setTimeout(() => setActiveCard(null), 1200);
  };

  React.useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById('solutions');
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Check if section is in viewport
      if (rect.top > windowHeight || rect.bottom < 0) return;

      const totalPoints = solutionPoints.length;

      // Calculate how much user scrolled inside the section
      const scrollProgress =
        (windowHeight - rect.top) / (rect.height + windowHeight);

      // Convert progress → index
      let index = Math.floor(scrollProgress * totalPoints);

      // Clamp index between 0 and last
      index = Math.max(0, Math.min(index, totalPoints - 1));

      updateActivePoint(index);
    };

    window.addEventListener('scroll', handleScroll);

    // Initialize
    updateActivePoint(0);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="payaxis-page">
      {/* HERO SECTION */}
      <section className="payloi-hero">
        <div className="hero-bg-payloi"></div>
        <div className="hero-grid"></div>
        <div className="hero-blob blob1"></div>
        <div className="hero-blob blob2"></div>
        <div className="hero-bg-text">PAYROLL</div>

        <div className="hero-inner-payloi">
          <div className="eyebrow">
            <div className="eyebrow-bar"></div>
            Run It Right with PeopleX
          </div>

          <h1 className="hero-h1">
            <div className="hl"><span className="hl-inner" style={{ '--i': 0 }}>No delays.</span></div>
            <div className="hl"><span className="hl-inner" style={{ '--i': 1 }}><span className="green-italic">No errors.</span></span></div>
            <div className="hl"><span className="hl-inner" style={{ '--i': 2 }}>No compromises.</span></div>
          </h1>
        </div>

        <div className="hero-bottom">
          <p class="hero-desc"><strong>PeopleX HR</strong> handles your entire payroll lifecycle — from salary processing to statutory compliance — with <strong>zero errors</strong> and <strong>full automation</strong>. Built for Indian businesses that refuse to settle.</p>
          <div className="hero-nums">
            <div className="hnum">
              <div className="hnum-big">10,000<span>+</span></div>
              <div className="hnum-label">Payslips / Month</div>
            </div>
            <div className="hnum">
              <div className="hnum-big">100</div>
              <div className="hnum-label">Clients</div>
            </div>
            <div className="hnum">
              <div className="hnum-big">99.9<span>%</span></div>
              <div className="hnum-label">Accuracy Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <div className="marquee-strip">
        <div className="m-inner">
          <div className="m-text">
            PAYROLL PROCESSING <span className="md"></span>
            STATUTORY COMPLIANCE <span className="md"></span>
            TDS FILING <span className="md"></span>
            FORM 16 AUTOMATION <span className="md"></span>
            ESS PORTAL <span className="md"></span>
            ATTENDANCE MANAGEMENT <span className="md"></span>
            FULL & FINAL SETTLEMENT <span className="md"></span>
            PF · ESI · PT · LWF <span className="md"></span>
            AWS HOSTED <span className="md"></span>
            SOC CERTIFIED <span className="md"></span>
            ISO 27001 <span className="md"></span>
            BANK INTEGRATION <span className="md"></span>
          </div>
          <div className="m-text">
            PAYROLL PROCESSING <span className="md"></span>
            STATUTORY COMPLIANCE <span className="md"></span>
            TDS FILING <span className="md"></span>
            FORM 16 AUTOMATION <span className="md"></span>
            ESS PORTAL <span className="md"></span>
            ATTENDANCE MANAGEMENT <span className="md"></span>
            FULL & FINAL SETTLEMENT <span className="md"></span>
            PF · ESI · PT · LWF <span className="md"></span>
            AWS HOSTED <span className="md"></span>
            SOC CERTIFIED <span className="md"></span>
            ISO 27001 <span className="md"></span>
            BANK INTEGRATION <span className="md"></span>
          </div>
        </div>
      </div>

      {/* PROBLEM SECTION */}
      <section className="problem">
        <div className="prob-head">
          <div className="hero-label">Your current payroll reality</div>
          <h2 className="section-title">The numbers <span className="green">don't lie.</span></h2>
        </div>

        <div className="prob-grid">
          <div className="prob-cell">
            <div className="pbig">67%</div>
            <div className="psub">Errors from manual entry</div>
            <p>Two-thirds of all payroll mistakes trace back to spreadsheet data entry — a fixable problem costing companies millions in corrections and employee trust.</p>
          </div>
          <div className="prob-cell">
            <div className="pbig">₹2.4L</div>
            <div className="psub">Avg compliance penalty</div>
            <p>A single missed PF or ESI deadline snowballs into notices, interest, and penalties. Statutory rules change — your spreadsheets don't keep up.</p>
          </div>
          <div className="prob-cell">
            <div className="pbig">40%</div>
            <div className="psub">HR time lost to queries</div>
            <p>Your People team spends nearly half their week answering the same payslip and tax questions. That time belongs to strategy, not admin.</p>
          </div>
        </div>
        <div className="prob-grid">
          <div className="prob-cell">
            <div className="pbig">3.2d</div>
            <div className="psub">Average payroll delay</div>
            <p>Multiple revision loops, missing inputs, delayed approvals — the average Indian payroll runs 3+ days late. That has a real cost on morale.</p>
          </div>
          <div className="prob-cell">
            <div className="pbig">58%</div>
            <div className="psub">Unencrypted payroll data</div>
            <p>More than half of companies share sensitive salary data via unprotected email attachments. One breach undoes years of trust.</p>
          </div>
        </div>
      </section>

      {/* SOLUTIONS SECTION */}
      <section className="solutions" id="solutions">
        <div className="hero-label">How we fix it</div>
        <h2 className="section-title">Pain points, <span className="green">precisely</span><br />addressed.</h2>
        <div className="sol-layout">
          <div className="sol-sticky">
            <p>Every PeopleX HR feature was built in direct response to a real payroll failure. We don't add features — we remove problems.</p>
            <div className="sol-prog">
              <div className="sp active" id="sp0" onClick={() => handleCardClick(0)}>01 — Payroll Automation</div>
              <div className="sp" id="sp1" onClick={() => handleCardClick(1)}>02 — Compliance Engine</div>
              <div className="sp" id="sp2" onClick={() => handleCardClick(2)}>03 — Employee Self-Service</div>
              <div className="sp" id="sp3" onClick={() => handleCardClick(3)}>04 — Attendance & Leave</div>
              <div className="sp" id="sp4" onClick={() => handleCardClick(4)}>05 — Analytics & Reports</div>
              <div className="sp" id="sp5" onClick={() => handleCardClick(5)}>06 — Integrations</div>
            </div>
          </div>
          <div className="sol-cards">
            <div className="scard" id="card-0" data-n="01" data-i="0" onClick={() => handleCardClick(0)}>
              <div className={`sc-icon ${activeCard === 'sp0' ? 'active' : ''}`}>⚙️</div>
              <h3>Intelligent Payroll Automation</h3>
              <div className="pain-row">Manual data entry causes 67% of all payroll errors — every single month.</div>
              <div className="sol-row">Formula-driven engine computes gross-to-net with zero manual intervention.</div>
              <p>PeopleX HR's calculation engine handles multi-pay-group logic, component formulas, CTC restructuring, loss-of-pay, ESOP, and Full & Final — configured once, runs flawlessly every cycle.</p>
              <div className="sc-pills">
                <span className="sc-pill">Formula Builder</span>
                <span className="sc-pill">Multi Pay Groups</span>
                <span className="sc-pill">Gross-to-Net</span>
                <span className="sc-pill">F&F Automation</span>
                <span className="sc-pill">Form 24Q</span>
              </div>
            </div>
            <div className="scard" id="card-1" data-n="02" data-i="1" onClick={() => handleCardClick(1)}>
              <div className="sc-icon">⚖️</div>
              <h3>Real-Time Compliance Engine</h3>
              <div className="pain-row">One missed deadline = ₹2.4L average penalty plus legal exposure.</div>
              <div className="sol-row">Auto-generates PF, ESI, PT, LWF challans with live statutory alerts.</div>
              <p>Our compliance engine monitors regulatory changes in real time. Challans are auto-prepared and validated. Notices get documented response support. Your compliance calendar runs itself.</p>
              <div className="sc-pills">
                <span className="sc-pill">PF Challans</span>
                <span className="sc-pill">ESI & IP Numbers</span>
                <span className="sc-pill">PT All States</span>
                <span className="sc-pill">LWF</span>
                <span className="sc-pill">Notice Support</span>
              </div>
            </div>
            <div className="scard" id="card-2" data-n="03" data-i="2" onClick={() => handleCardClick(2)}>
              <div className="sc-icon">📱</div>
              <h3>Employee Self-Service Portal</h3>
              <div className="pain-row">HR spends 40% of their week on questions employees could answer themselves.</div>
              <div className="sol-row">Employees self-resolve 90% of payroll needs — on any device, anywhere.</div>
              <p>A modern ESS portal covering 16 payroll aspects: payslip access, investment declarations, reimbursement claims, Form 16 download, and query tracking — all mobile-first.</p>
              <div className="sc-pills">
                <span className="sc-pill">Investment Declaration</span>
                <span className="sc-pill">Form 16 Download</span>
                <span className="sc-pill">Reimbursements</span>
                <span className="sc-pill">Query Management</span>
                <span className="sc-pill">Mobile First</span>
              </div>
            </div>
            <div className="scard" id="card-3" data-n="04" data-i="3" onClick={() => handleCardClick(3)}>
              <div className="sc-icon">📍</div>
              <h3>Attendance & Leave Management</h3>
              <div className="pain-row">Manual attendance reconciliation creates LOP errors that recur every cycle.</div>
              <div className="sol-row">GPS-verified mobile punch-in flows directly into payroll calculation.</div>
              <p>Geofenced mobile attendance, intelligent shift scheduling, multi-level leave approval workflows, and timesheet management — tightly integrated with payroll so LOP is always right.</p>
              <div className="sc-pills">
                <span className="sc-pill">Geotagged Punch-in</span>
                <span className="sc-pill">Shift Scheduling</span>
                <span className="sc-pill">Leave Workflows</span>
                <span className="sc-pill">Timesheets</span>
              </div>
            </div>
            <div className="scard" id="card-4" data-n="05" data-i="4" onClick={() => handleCardClick(4)}>
              <div className="sc-icon">📊</div>
              <h3>Live Analytics & On-Demand Reports</h3>
              <div className="pain-row">Zero visibility until month-end — decisions made blind, every cycle.</div>
              <div className="sol-row">Real-time dashboards and 50+ on-demand reports available cloud-anytime.</div>
              <p>Track payroll milestones live, analyse employee query trends, generate GL entries, download bank advice, and benchmark headcount costs — all in real time, no batch waits.</p>
              <div className="sc-pills">
                <span className="sc-pill">Milestone Tracking</span>
                <span className="sc-pill">GL Integration</span>
                <span className="sc-pill">Query Analytics</span>
                <span className="sc-pill">50+ Reports</span>
                <span className="sc-pill">Cloud Anytime</span>
              </div>
            </div>
            <div className="scard" id="card-5" data-n="06" data-i="5" onClick={() => handleCardClick(5)}>
              <div className="sc-icon">🔗</div>
              <h3>Seamless HRMS Integrations</h3>
              <div className="pain-row">Data silos between HR and Finance create reconciliation nightmares monthly.</div>
              <div className="sol-row">Bi-directional connectors eliminate data silos at source, from day one.</div>
              <p>Pre-built HR connectors, ERP integrations, PSS/CRM hooks, and encrypted bank file upload for direct salary disbursement. Structured input templates enforce clean data from day one.</p>
              <div className="sc-pills">
                <span className="sc-pill">HR Connector</span>
                <span className="sc-pill">ERP Integration</span>
                <span className="sc-pill">Encrypted Bank File</span>
                <span className="sc-pill">PSS / CRM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM SECTION */}
      <section className="platform" id="platform">
        <div>
          <div className="hero-label">The Platform</div>
          <h2 className="section-title">One platform.<br /><span className="green">Everything.</span></h2>
        </div>
        <div className="pg">
          {/* First Row: 2 cards (5-7 columns) */}
          <div className="pc-row pc-row-1">
            <div className="pc pc1">
              <div className="pc-tag">01</div>
              <h4>Formula-Based Salary Engine</h4>
              <p>Visual formula builder for any CTC structure. No code required.</p>
              <div className="mbars">
                <div className="mb" style={{ '--bar-height': '30%' }}></div>
                <div className="mb" style={{ '--bar-height': '55%' }}></div>
                <div className="mb" style={{ '--bar-height': '45%' }}></div>
                <div className="mb" style={{ '--bar-height': '80%' }}></div>
                <div className="mb" style={{ '--bar-height': '65%' }}></div>
                <div className="mb" style={{ '--bar-height': '92%' }}></div>
                <div className="mb" style={{ '--bar-height': '78%' }}></div>
                <div className="mb" style={{ '--bar-height': '100%' }}></div>
              </div>
            </div>
            <div className="pc pc2">
              <div className="pc-tag">02</div>
              <h4>Live Compliance Status</h4>
              <p>Real-time view of all statutory obligations with deadline countdowns and auto-alert escalation before any breach occurs.</p>
              <div className="sdots">
                <div className="sd-row"><div className="sd g"></div>PF Challan — Filed & Verified</div>
                <div className="sd-row"><div className="sd g"></div>ESI Return — Filed on time</div>
                <div className="sd-row"><div className="sd b"></div>Form 24Q — Processing Q3</div>
                <div className="sd-row"><div className="sd y"></div>PT Maharashtra — Due in 4 days</div>
              </div>
            </div>
          </div>

          {/* Second Row: 3 cards (4-4-4 columns) */}
          <div className="pc-row pc-row-2">
            <div className="pc pc3">
              <div className="pc-tag">03</div>
              <h4>Mobile ESS</h4>
              <p>Employees manage their entire payroll journey from any device.</p>
              <div className="mring">
                <div className="mring-i"></div>
                <div className="mring-label">72%</div>
              </div>
              <p style={{ marginTop: '10px', fontSize: '.75rem', color: 'rgba(255, 255, 255, 0.25)' }}>query deflection via ESS</p>
            </div>
            <div className="pc pc4">
              <div className="pc-tag">04</div>
              <h4>Maker-Checker Controls</h4>
              <p>Every payroll action requires dual-approval with full timestamped audit trails for complete finance traceability.</p>
            </div>
            <div className="pc pc5">
              <div className="pc-tag">05</div>
              <h4>Cloud Reports</h4>
              <p>50+ payroll, statutory and HR reports — on demand, cloud-anywhere, zero batch wait.</p>
            </div>
          </div>

          {/* Third Row: 2 cards (7-5 columns) */}
          <div className="pc-row pc-row-3">
            <div className="pc pc6">
              <div className="pc-tag">06</div>
              <h4>Encrypted Bank Disbursement</h4>
              <p>Salary upload via encrypted file to all major Indian banks. Automatic reconciliation. On-time every time with full bank advice and payment confirmation.</p>
              <div className="sdots" style={{ marginTop: '20px' }}>
                <div className="sd-row"><div className="sd g"></div>HDFC — 1,240 transfers initiated</div>
                <div className="sd-row"><div className="sd g"></div>ICICI — 876 transfers confirmed</div>
                <div className="sd-row"><div className="sd b"></div>SBI — 540 transfers in progress</div>
              </div>
            </div>
            <div className="pc pc7">
              <div className="pc-tag">07</div>
              <h4>Geotagged Attendance</h4>
              <p>Mobile punch-in with GPS geofencing. Feeds directly into LOP calculation.</p>
              <div className="mbars" style={{ marginTop: '20px' }}>
                <div className="mb" style={{ '--bar-height': '90%', background: 'linear-gradient(180deg, var(--navy), var(--navy-dark))' }}></div>
                <div className="mb" style={{ '--bar-height': '85%', background: 'linear-gradient(180deg, var(--navy), var(--navy-dark))' }}></div>
                <div className="mb" style={{ '--bar-height': '97%', background: 'linear-gradient(180deg, var(--navy), var(--navy-dark))' }}></div>
                <div className="mb" style={{ '--bar-height': '78%', background: 'linear-gradient(180deg, var(--navy), var(--navy-dark))' }}></div>
                <div className="mb" style={{ '--bar-height': '100%', background: 'linear-gradient(180deg, var(--navy), var(--navy-dark))' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECURITY SECTION */}
      <section className="security" id="security">
        <div className="sec-inner">
          <div className="sec-shield">
            <div className="shield-svg-wrap">
              <svg viewBox="0 0 200 220" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
                <ellipse cx="100" cy="110" rx="94" ry="94" stroke="rgba(0,207,255,.1)" strokeWidth="1" strokeDasharray="3 7" />
                <ellipse cx="100" cy="110" rx="70" ry="70" stroke="rgba(0,207,255,.06)" strokeWidth="1" strokeDasharray="2 5" />
                <path d="M100 15 L168 46 L168 112 Q168 164 100 200 Q32 164 32 112 L32 46 Z" fill="rgba(0,85,170,.22)" stroke="rgba(0,207,255,.45)" strokeWidth="1.5" />
                <path d="M100 36 L153 61 L153 112 Q153 150 100 180 Q47 150 47 112 L47 61 Z" fill="rgba(0,153,221,.1)" stroke="rgba(0,207,255,.2)" strokeWidth="1" />
                <rect x="83" y="96" width="34" height="27" rx="4" fill="rgba(0,207,255,.14)" stroke="rgba(0,207,255,.5)" strokeWidth="1.5" />
                <path d="M91 96 Q91 81 100 81 Q109 81 109 96" stroke="rgba(0,207,255,.5)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                <circle cx="100" cy="110" r="3" fill="var(--navy-light)" />
                <line x1="100" y1="113" x2="100" y2="118" stroke="var(--navy-light)" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="100" cy="16" r="5" fill="var(--navy-light)" opacity=".8" />
                <circle cx="193" cy="110" r="4" fill="var(--green)" opacity=".7" />
                <circle cx="7" cy="110" r="4" fill="var(--green)" opacity=".7" />
                <circle cx="100" cy="204" r="5" fill="var(--navy-light)" opacity=".5" />
                <circle cx="100" cy="110" r="42" fill="rgba(0,207,255,.03)" />
              </svg>
            </div>
            <div className="cert-row">
              <div className="cert">Built with industry best practices</div>
            </div>
          </div>
          <div className="sec-content">
            <div className="hero-label">Security & Infrastructure</div>
            <h2 className="section-title">Fortress-grade<br /><span className="green" style={{ color: 'var(--navy-light)' }}>protection.</span></h2>
            <div className="sec-stats">
              <div className="ss"><div className="ss-n">1hr</div><div className="ss-l">Recovery Point Objective</div></div>
              <div className="ss"><div className="ss-n">1d</div><div className="ss-l">Recovery Time Objective</div></div>
              <div className="ss"><div className="ss-n">AWS</div><div className="ss-l">Mumbai Primary + Singapore DR</div></div>
              <div className="ss"><div className="ss-n">GFS</div><div className="ss-l">Daily · Weekly · Monthly Backups</div></div>
            </div>
            <div className="sec-list">
              <div className="si"><div className="si-ic">🛡️</div>Multi-layer encryption for all payroll data at rest and in transit. Zero unencrypted data, ever.</div>
              <div className="si"><div className="si-ic">👁️</div>24/7 NOC monitoring with unified threat management across all servers in high-availability mode.</div>
              <div className="si"><div className="si-ic">🔐</div>Role-based access with Maker-Checker approval chains and full timestamped audit trail on every action.</div>
              <div className="si"><div className="si-ic">🗄️</div>Client data purged from archives upon confirmed receipt — full data sovereignty, no lock-in.</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="process" id="process">
        <div className="hero-label">The 7-Step Payroll Cycle</div>
        <h2 className="section-title">How a perfect payroll<br /><span className="green">cycle runs.</span></h2>
        <div className="proc-tl">
          <div className="pp-proc-line"></div>
          <div className="pp-proc-steps">
            <div className="pstep">
              <div className="pnode">01</div>
              <h5>Data Input</h5>
              <p>Structured templates with auto-validation. Upload once, validated on submission.</p>
            </div>
            <div className="pstep">
              <div className="pnode">02</div>
              <h5>Validation</h5>
              <p>PAN, UAN, bank account checks. Duplicates and gaps flagged instantly.</p>
            </div>
            <div className="pstep">
              <div className="pnode">03</div>
              <h5>Processing</h5>
              <p>Engine computes all components — gross, deductions, compliance — automatically.</p>
            </div>
            <div className="pstep">
              <div className="pnode">04</div>
              <h5>Draft Review</h5>
              <p>Draft pay sheet shared. Client reviews or signs off in one click.</p>
            </div>
            <div className="pstep">
              <div className="pnode">05</div>
              <h5>Finalisation</h5>
              <p>Payroll locked. Challans generated and validated. Bank file prepared.</p>
            </div>
            <div className="pstep">
              <div className="pnode">06</div>
              <h5>Disbursement</h5>
              <p>Encrypted bank file uploaded. Salaries hit accounts on schedule.</p>
            </div>
            <div className="pstep">
              <div className="pnode">07</div>
              <h5>Reports</h5>
              <p>GL, statutory reports, analytics live on cloud. No waiting.</p>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}

export default PayloiProduct;
